<script lang="ts" setup>
import type { TripMonitorTripStop, TripMonitorTripStopWithId } from "@/api/trips/dto/trip-monitor";
import TripStop from "@/views/trips/monitor/components/TripStop.vue";
import { computed } from "vue";
import { useOrder } from "@/composables";
import { useI18n } from "vue-i18n";

const { generateId } = useOrder();
const { t } = useI18n();

const maxVisibleStops = 5;

const props = defineProps<{
  tripStops: TripMonitorTripStop[];
}>();

const stops = computed<TripMonitorTripStopWithId[]>(() => {
  return props.tripStops.map((tripStop) => {
    return {
      ...tripStop,
      id: generateId(),
    };
  });
});

const stopsData = computed<{
  visibleStarts: TripMonitorTripStopWithId[];
  visibleEnds: TripMonitorTripStopWithId[];
  hiddenStops: TripMonitorTripStopWithId[];
  hiddenCount: number;
}>(() => {
  const total = stops.value.length;

  if (total <= maxVisibleStops) {
    return {
      visibleStarts: stops.value,
      hiddenStops: [],
      visibleEnds: [],
      hiddenCount: 0,
    };
  }

  const visibleStarts = stops.value.slice(0, 2);
  const hiddenStops = stops.value.slice(2, -2);
  const visibleEnds = stops.value.slice(-2);

  const hiddenCount = total - 4;

  return {
    visibleStarts,
    visibleEnds,
    hiddenStops,
    hiddenCount,
  };
});
</script>

<template>
  <div class="d-flex align-center col-gap-2">
    <trip-stop
      v-for="(tripStop, index) of stopsData.visibleStarts"
      :key="tripStop.id"
      :show-divider="index !== stopsData.visibleStarts.length - 1"
      :trip-stop="tripStop"
      :trip-stops="stops"
    />

    <v-menu v-if="stopsData.hiddenCount" :close-on-content-click="false" location="top center" offset="20">
      <template #activator="{ props: menuProps }">
        <div class="d-flex align-center">
          <div class="trip-stops__divider"></div>
          <v-btn class="text-lowercase" color="primary" size="small" v-bind="menuProps" variant="text">
            (+{{ stopsData.hiddenCount }} {{ t("general.more") }})
          </v-btn>
          <div class="trip-stops__divider"></div>
        </div>
      </template>

      <div class="bg-white pa-3 d-flex col-gap-2 elevation-3">
        <trip-stop
          v-for="(tripStop, index) of stopsData.hiddenStops"
          :key="tripStop.id"
          :show-divider="index !== stopsData.hiddenStops.length - 1"
          :trip-stop="tripStop"
          :trip-stops="stops"
        />
      </div>
    </v-menu>

    <template v-if="stopsData.visibleEnds.length">
      <trip-stop
        v-for="(tripStop, index) of stopsData.visibleEnds"
        :key="tripStop.id"
        :show-divider="index !== stopsData.visibleStarts.length - 1"
        :trip-stop="tripStop"
        :trip-stops="stops"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trip-stops {
  &__divider {
    width: 20px;
    height: 2px;
    background-color: $grey-lighten-2;
  }
}
</style>
